// Client-side product analytics: Plausible custom events.
//
// Server-rendered page and station views go to BigQuery through
// StationAnalytics::Recorder instead. The split is by what the event IS, not by
// preference: the recorder builds rows keyed to a station or a location the
// controller already loaded, so it fits page requests and API calls. In-page
// interactions carry none of that and would need a server round-trip per click
// to be recorded there, which is not worth it for a UI toggle.
//
// Plausible's script is production-only (see the layout), so these are no-ops in
// development — the optional call is what makes that safe rather than a
// ReferenceError. The queue stub in the layout head means events fired before
// plausible.js finishes loading are replayed rather than dropped.

function track(name, props) {
  try {
    window.plausible?.(name, { props })
  } catch {
    // Analytics must never break the interaction that triggered it.
  }
}

// ---- A map that died after it had been working ----------------------------
//
// Randy, on a phone, 2026-09-11: minutes of panning /explore in Depth mode
// maximized, then the map is replaced by "The map could not be loaded." That
// string is MapFallback, which means the ErrorBoundary caught a throw — and
// `componentDidCatch` only console.errored it, so on a phone the actual error
// was invisible. A screenshot was the entire evidence.
//
// This makes the next occurrence a record. It is deliberately NOT the error's
// text: `Chart Error` above states the rule this file runs on — the reason
// leaves, the text does not — and a React error message can carry a URL, a
// station name, or whatever a component was rendering when it threw. Two
// clamped enums instead, which is enough to answer the questions that decide
// what to do next: which surface, and whether it looks like the GPU.
//
// `Chart Error` does not cover this and could not have: it is gated on
// `chartMode`, so /explore — where this happened — emits nothing at all.
// ENRICHED 2026-09-11, after the first version proved it had recorded that a
// crash happened and nothing about the conditions. Randy's ask: a crash on the
// boat should be a complete report without him taking notes.
//
// The extra properties come from _mapCrashContext.js, which the map publishes to
// as it moves and this reads at catch time. Every one is a BUCKET or an enum —
// an exact zoom beside an exact age and a timestamp is a per-visit record, which
// is the thing a cookieless aggregate tracker must not carry.
//
// `gl_lost` is the one that cannot be derived from the error. On iOS the
// context can go away without the resulting throw mentioning WebGL at all — an
// allocation failure inside a render reads as an ordinary TypeError — so
// `reason` alone cannot separate a GPU death from a bug in our code. A latched
// canvas listener can.
export const MAP_SURFACES = ['explore', 'chart', 'station', 'home', 'go']
export const MAP_CRASH_REASONS = ['webgl', 'chunk', 'render', 'unknown']

export function trackMapCrash(surface, reason, context = {}) {
  if (!MAP_SURFACES.includes(surface)) return
  track('Map Crash', {
    // Context FIRST, so the two properties this event is identified by are the
    // ones that survive. The context is built from clamped buckets, but it
    // still arrives from a caller, and a caller must not be able to relabel
    // which map crashed or why.
    ...context,
    surface,
    reason: MAP_CRASH_REASONS.includes(reason) ? reason : 'unknown',
  })
}

// The crash with no boundary: iOS killing the whole tab rather than the GL
// context. The page reloads itself and nothing catches anything, so the only
// evidence is that a tab which had a map running came back without having said
// goodbye — see detectUncleanReload for why that is evidence rather than proof,
// and for what else looks the same.
export function trackMapReload(surface) {
  if (!MAP_SURFACES.includes(surface)) return
  track('Map Reload', { surface, kind: 'unclean' })
}

// WHICH DOOR PEOPLE USE INTO GO MODE.
//
// The plan's open question, and the only one analytics can settle: the most
// COMMON entry is expected to be the home map (returning readers) and the
// highest-VALUE one a chart page (a new reader with a boat under them). Both
// get the same button, so the only way to learn which is actually used is to
// record which surface the reader was on when they pressed it.
//
// A NEW EVENT NAME, and every existing one is untouched. `Explore Mode` and
// `Explore Layers` keep firing with their old values so a year of history stays
// one series — and neither of them fires from inside Go Mode, which has no
// picker and is not someone choosing a map type.
//
// The surface list is clamped for the same reason `Map Crash` clamps its own: a
// caller that forgets, or mistypes, reports nothing rather than opening a row
// nobody can interpret. It grows as the door is added to chart and station
// pages (plan items 3 and 4). 'station' arrived with item 4 and 'chart' with
// item 3, and each is ONE value for a page type with more than one state: a
// tide station page and a current station page are the same door, and a chart
// page embedded and maximized is the same door on the same page. Only 'home'
// is rewritten at press time (`goEntrySurface`), because since #433 maximizing
// it genuinely changes which surface the reader is on.
export const GO_MODE_SURFACES = ['home', 'explore', 'station', 'chart']

export function trackGoMode(surface) {
  if (!GO_MODE_SURFACES.includes(surface)) return
  track('Go Mode', { surface })
}

// Which Explore mode a visitor actually used.
//
// `source` separates intent from arrival: "switch" is someone clicking the tab,
// "link" is a shared or bookmarked URL that opened straight into that mode.
// Without it the two are indistinguishable, and they answer different questions
// — one is whether the tab is discoverable, the other whether the mode is worth
// sharing.
//
// Landing on the default mode is not a use of it, so that case fires nothing:
// it would just duplicate the /explore pageview Plausible already counts, on
// every single visit.
export function trackExploreMode(mode, source) {
  track('Explore Mode', { mode, source })
}

// WHICH LAYER a visitor turned on or off (docs/explore-layers-design.md).
//
// `Explore Mode` answered a four-way question that no longer exists: the map
// has a GROUND and at most one VISUALIZATION, and "depth" was both an answer to
// the first and an absence of the second. That event keeps firing with its old
// values so a year of history stays one series; this one carries what actually
// happened.
//
// One event per tile TOUCHED, not one per resulting state: turning Moving Tides
// off and Weather Radar on is two facts, and collapsing them into a single
// "radar" row loses the one that says people turn things off.
export const EXPLORE_LAYERS = ['standard', 'chart', 'tides', 'currents', 'wind', 'radar']
export const EXPLORE_LAYER_STATES = ['on', 'off']
export const EXPLORE_LAYER_SOURCES = ['switch', 'link', 'default']

export function trackExploreLayers(layer, state, source) {
  if (!EXPLORE_LAYERS.includes(layer)) return
  if (!EXPLORE_LAYER_STATES.includes(state)) return
  if (!EXPLORE_LAYER_SOURCES.includes(source)) return
  track('Explore Layers', { layer, state, source })
}

// Whether the map's time controls are used at all.
//
// `Explore Mode` says which map someone opened; it cannot say whether the
// playhead under that map was ever touched. That is the question this answers,
// and it is deliberately the WHOLE question: which control (`action`) on which
// map (`mode`), and nothing about the time it was moved to. A slider position,
// a date, an offset or a speed value beside a timestamp is a behavioural record
// of one visitor, which is exactly what a cookieless aggregate tracker is not
// for — and the shape of the curve someone scrubbed is not a product question
// anyone here has.
//
// Read it two ways. Plausible's `visitors` on this event answers "how many
// people use the controls"; `events` per mode/action is bounded repeat
// intensity — a second deliberate scrub in one visit is real use and is not
// suppressed. What IS suppressed is intermediate slider values (one committed
// gesture is one event) and every programmatic state change; see
// explore/_timelineAnalytics.js, which owns those boundaries.
//
// Depth is absent from the modes on purpose: it is bathymetry, it has no time
// axis, and its mode does not render the player at all. Chart pages are absent
// for the same reason — they mount in depth.
export const EXPLORE_TIMELINE_MODES = ['tides', 'currents', 'radar']

export const EXPLORE_TIMELINE_ACTIONS = ['scrub', 'play', 'pause', 'now', 'speed']

// Both values are clamped rather than passed through, and an out-of-bounds
// value drops the whole event instead of widening it — the same rule the chart
// events follow. `mode` in particular comes from ExploreMap's bounded vizMode
// and never from a label, which is localized in twelve languages.
export function trackExploreTimelineUse(mode, action) {
  if (!EXPLORE_TIMELINE_MODES.includes(mode)) return
  if (!EXPLORE_TIMELINE_ACTIONS.includes(action)) return
  track('Explore Timeline Use', { mode, action })
}

// ---------------------------------------------------------------------------
// Chart page events.
//
// Four events, and the four names are a CONTRACT: the importer downstream reads
// them verbatim, and Plausible goals are created by exact name. Renaming one
// here silently ends a series rather than moving it.
//
// What these answer that nothing else can. Pageviews prove someone arrived and
// Search Console proves how; neither can say whether the map ever became a
// chart. `Chart Ready` and `Chart Error` are two halves of one question, which
// is why they carry the same `catalog` and why the ratio between them —
// Error / (Ready + Error) — is the number worth watching. The two select events
// answer the other half: whether the reader used the thing a chart page has and
// a station page does not.
//
// EVERY property here is an enum or a public catalog number, and that is the
// privacy rule, not a coincidence. Plausible's own model is cookieless and
// aggregate; a free-text property (an error message, a station name, a
// permalink) would put a per-visit string beside a timestamp and undo that. So
// values are clamped against the lists below rather than passed through, and a
// value that does not clamp drops the whole event instead of widening it.
// ---------------------------------------------------------------------------

const CATALOGS = ['noaa', 'ukho']

// The `reason` enum. `unknown` is not padding: MapLibre raises a lot of shapes
// and a bucket for "we could not tell" is what stops the other four from
// quietly absorbing errors they do not describe.
export const CHART_ERROR_REASONS = ['map_init', 'style', 'source', 'webgl', 'unknown']

export const CHART_STATION_TYPES = ['tide', 'current']

// The `feature_type` enum. It is a chart reader's vocabulary, not the map's:
// the composed stack draws ~60 clickable layers over six archives, and the
// question this event answers is which KIND of thing a reader asked about.
// furnitureFeatureType() in _marine/furnitureAnalytics.js is the only thing
// allowed to produce these, and `other` is where everything the list does not
// name goes on purpose — see that file for which categories those are.
export const CHART_FEATURE_TYPES = [
  'light',
  'buoy',
  'beacon',
  'harbor',
  'marina',
  'anchorage',
  'wreck',
  'rock',
  'obstruction',
  'fairway',
  'track',
  'other',
]

// A vendor id is public (it is in the URL) and there are ~900 of them, but the
// bound is asserted rather than assumed: anything that is not a short
// alphanumeric catalog number is something this function did not expect, and a
// surprise reaching Plausible is a surprise reaching the bill.
const CHART_ID = /^[a-z0-9]{1,12}$/

// The shared half of every event below: the chart, and which catalog it is
// from. Returns null when either is missing or out of bounds, which is the
// caller's signal to emit nothing at all.
function chartProps(analytics) {
  if (!analytics) return null
  const chartId = String(analytics.chart_id ?? '').toLowerCase()
  const catalog = String(analytics.catalog ?? '')
  if (!CHART_ID.test(chartId)) return null
  if (!CATALOGS.includes(catalog)) return null
  return { chart_id: chartId, catalog }
}

// The chart drew. Fires once per chart-page mount, from the one boundary in
// _chartAnalytics.js — see the note there for what "ready" is allowed to mean.
export function trackChartReady(analytics) {
  const props = chartProps(analytics)
  if (!props) return
  track('Chart Ready', props)
}

// The chart did not draw, and will not. `reason` is clamped to the enum, so an
// exception object, a message, or a novel string all arrive as `unknown` — the
// error's TEXT never leaves the browser.
export function trackChartError(analytics, reason) {
  const props = chartProps(analytics)
  if (!props) return
  track('Chart Error', {
    ...props,
    reason: CHART_ERROR_REASONS.includes(reason) ? reason : 'unknown',
  })
}

// A click that opened a chart-furniture popup. `featureType` comes from
// furnitureFeatureType(), which is exhaustive and already falls back to
// 'other'; the clamp here is the second half of that guarantee, for a caller
// that does not use it.
export function trackChartFeatureSelect(analytics, featureType) {
  const props = chartProps(analytics)
  if (!props) return
  track('Chart Feature Select', {
    ...props,
    feature_type: CHART_FEATURE_TYPES.includes(featureType) ? featureType : 'other',
  })
}

// A click that opened a tide or current station from the chart. No station id
// and no station name: which KIND of station a chart reader opens is the
// product question, and the individual station is the part that would make this
// a behavioural record of one visitor.
export function trackChartStationSelect(analytics, stationType) {
  const props = chartProps(analytics)
  if (!props) return
  if (!CHART_STATION_TYPES.includes(stationType)) return
  track('Chart Station Select', { ...props, station_type: stationType })
}

// ROUTE PLANNING on /explore (2026-10-23): one event per route request that
// finished, with how it finished. The funnel question is whether people who
// plan a route go on to the app, so the outcome is what matters; the points
// themselves are never sent.
export const ROUTE_PLAN_OUTCOMES = [
  'ok',
  'on_land',
  'no_route',
  'no_snap',
  'outside_area',
  'timeout',
  'unavailable',
  'busy',
]

export function trackRoutePlan(outcome) {
  if (!ROUTE_PLAN_OUTCOMES.includes(outcome)) outcome = 'unavailable'
  track('Route Plan', { outcome })
}
